import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, BookButtons } from "../components/sections";
import { PILLARS, IMAGES } from "../data/site";

const TOPICS = [
  { title: "Driving Fear", text: "Fear while driving is a learned response — often from a bad experience, a poor first trainer, or years of not driving. Step-by-step real-road practice retrains that response." },
  { title: "Traffic Anxiety", text: "Busy roads can overload your mind. We teach you what to focus on, when to check mirrors, and what to ignore — so traffic feels manageable." },
  { title: "Making Quick Road Decisions", text: "Hesitation at crossings and lane changes comes from unpractised decisions. We practise them on real roads until they become natural." },
  { title: "Attention & Focus", text: "Nervous drivers often watch the wrong things. We train you to look where it matters — ahead, around, and at real risks." },
  { title: "Road Awareness", text: "Good drivers understand what other road users may do — before they do it. This awareness can be learned with practice." },
  { title: "Emotional Control", text: "Honking, small mistakes and rude drivers can shake a nervous learner. We train you to stay calm and recover quickly." },
];

const Psychology = () => (
  <>
    <PageHero
      eyebrow="Driving Psychology"
      lines={["The Mind Is The", "Most Important", "Part Of Driving."]}
      text="DWC's specialty: simple, practical psychology-based methods that help you manage fear, traffic anxiety, focus and quick road decisions — explained in plain language, with no gimmicks."
      image={IMAGES.traffic}
      imageAlt="Real Indian traffic — the environment where driving psychology matters most"
    >
      <BookButtons bookTestId="psy-book-btn" waTestId="psy-whatsapp-btn" />
    </PageHero>

    <section className="bg-dwc-soft py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="The Six Pillars"
          number="01"
          title="What We Train In Your Mind"
          text="Driving is not only about controlling the car. Your mind also plays an important role. Every DWC training session builds these six mental skills along with your practical driving."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.num} delay={(i % 3) * 0.08} className="h-full">
              <article className="h-full rounded-2xl border border-dwc-green/10 bg-white p-8 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
                <p className="text-4xl font-black tracking-tight text-dwc-green/15">{p.num}</p>
                <h3 className="mt-3 text-lg font-extrabold tracking-tight text-dwc-charcoal">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/65">{p.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="In Practice"
          number="02"
          title="What Driving Psychology Actually Helps With"
          text="Clear, practical areas of mental training — explained in plain language, practised on real roads."
        />
        <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2">
          {TOPICS.map((t, i) => (
            <Reveal key={t.title} delay={(i % 2) * 0.1}>
              <div className="border-l-2 border-dwc-green pl-6">
                <h3 className="text-lg font-extrabold tracking-tight text-dwc-charcoal">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/65">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 rounded-3xl bg-dwc-green p-10 text-center md:p-14 grain relative overflow-hidden">
            <p className="text-2xl font-extrabold uppercase leading-snug tracking-tight text-white md:text-3xl text-balance">
              You don't have to be fearless to drive.
              <span className="mt-2 block text-emerald-200">You just need the right training and confidence.</span>
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <CTASection title="Train Your Mind For The Road." sub="Book an appointment and find out what is holding your driving back." chapter="03" />
  </>
);

export default Psychology;
